import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, CreditCard, Clock, Tag, TrendingUp, Lock, Info } from "lucide-react";

// Standalone pricing-confirmation page for admin-invited users. This is the
// landing destination after they verify their email + get admin approval —
// before any Paystack call is made. It renders the full 3-tier pricing
// timeline (3 months free → R149/month for 12 months → R299/month standard),
// requires the user to tick a "I understand & agree" checkbox, and then
// kicks off the existing /invite-trial/initialize endpoint on click.
//
// Deliberately NOT shared code with the existing Subscribe.jsx or the
// pre-existing InviteTrialSubscribe.jsx — this page is its own self-
// contained unit so edits to pricing copy here never risk changing the
// other payment pages.
export default function InvitePaymentPlan() {
  const { user } = useAuth();
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gatewayReady, setGatewayReady] = useState(true);
  const [roleLabel, setRoleLabel] = useState(user?.role || "member");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/invite-trial/status");
        if (cancelled) return;
        setGatewayReady(!!data.gateway_ready);
        if (data.role_label) setRoleLabel(data.role_label);
      } catch (e) {
        if (cancelled) return;
        toast.error(errMsg(e, "Could not load your invite details"));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = async () => {
    if (!agreeTerms || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/invite-trial/initialize");
      if (!data?.authorization_url) throw new Error("No authorization URL returned");
      // Full-page redirect to Paystack. Session cookies survive.
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start the subscription"));
      setSubmitting(false);
    }
  };

  const launchEndYear = new Date(Date.now() + (90 + 365) * 86400000).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-payment-plan-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-xl w-full shadow-sm fade-in">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-full bg-terracotta flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-forest/50">Invited {roleLabel}</p>
            <h1 className="text-2xl font-black text-forest leading-tight">Confirm your plan</h1>
          </div>
        </div>

        <p className="text-sm text-forest/70 mb-5">
          Here's exactly what you'll pay — now, during your launch year, and after. There are no surprises and you can cancel from your profile at any time.
        </p>

        <ol className="space-y-3 mb-6" data-testid="invite-payment-timeline">
          <li className="flex items-start gap-3 rounded-2xl bg-sage-light/60 border-2 border-sage/40 p-4" data-testid="invite-payment-tier-1">
            <Clock className="w-5 h-5 text-sage-dark shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-forest text-sm">First 3 months — <span className="text-sage-dark">R0 (free)</span></p>
              <p className="text-xs text-forest/70">We save your card today, but you aren't charged anything for the first 3 months.</p>
            </div>
          </li>
          <li className="flex items-start gap-3 rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/40 p-4" data-testid="invite-payment-tier-2">
            <Tag className="w-5 h-5 text-terracotta-dark shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-forest text-sm">Then <span className="text-terracotta-dark">R149 / month</span> for 12 months</p>
              <p className="text-xs text-forest/70">Launch pricing locks in on your first billing date and stays for a full year.</p>
            </div>
          </li>
          <li className="flex items-start gap-3 rounded-2xl bg-softstone/70 border-2 border-forest/15 p-4" data-testid="invite-payment-tier-3">
            <TrendingUp className="w-5 h-5 text-forest shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-forest text-sm">After that — <span className="text-forest">R299 / month</span> (standard)</p>
              <p className="text-xs text-forest/70">From {launchEndYear} your subscription automatically renews at the standard R299/month. We'll email you 5 days before the switch.</p>
            </div>
          </li>
        </ol>

        <div className="rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/40 p-4 mb-5" data-testid="invite-payment-terms-block">
          <p className="text-[11px] text-forest/80 leading-relaxed mb-3" data-testid="invite-payment-terms-text">
            I understand that Compass Kids will save my card today, give me <strong>3 months free</strong>, then charge <strong>R149 per month for 12 months</strong> starting on my first billing date, after which my subscription automatically moves to the <strong>standard rate of R299 per month</strong>. I can cancel from my profile at any time before each renewal.
          </p>
          <label className="flex items-start gap-2 text-xs font-bold text-forest cursor-pointer select-none" data-testid="invite-payment-terms-label">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
              data-testid="invite-payment-terms-checkbox"
            />
            <span>I have read and agree to the terms above.</span>
          </label>
        </div>

        <p
          className="text-[11px] text-forest/60 mb-3 flex items-start gap-1.5 leading-snug"
          data-testid="r1-verification-notice"
        >
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Paystack charges a refundable R1 card verification fee to activate your payment method. It's refunded in full and is not part of your subscription cost.</span>
        </p>
        <Button
          onClick={subscribe}
          disabled={!agreeTerms || submitting || !gatewayReady}
          className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-lg hover:-translate-y-0.5 transition-transform"
          data-testid="invite-payment-subscribe-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {submitting ? "Opening secure checkout…" : "Subscribe Now"}
        </Button>

        {!gatewayReady && (
          <p className="mt-3 text-xs text-center text-terracotta-dark" data-testid="invite-payment-gateway-off">
            Payment gateway is not available right now. Please try again later.
          </p>
        )}

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-forest/50 mt-5">
          <Lock className="w-3 h-3" />
          Secure checkout via Paystack · Cancel anytime from your profile
        </div>

        <p className="text-center text-xs text-forest/40 mt-5">
          Need to use a different account? <Link to="/login" className="font-bold text-terracotta-dark hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
